import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  Copy,
  Heart,
  Share2,
  Trash2,
  History,
  ChevronRight,
  Coffee,
  Star,
  Instagram,
  X,
} from "lucide-react";
import Hero from "@/components/Hero";
import Generator from "@/components/Generator";
import Categories from "@/components/Categories";
import HistoryFavoritesPanel from "@/components/HistoryFavoritesPanel";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let id = localStorage.getItem("psf_session_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("psf_session_id", id);
  }
  return id;
}

export default function Home() {
  const sessionId = useMemo(() => getSessionId(), []);
  const [categories, setCategories] = useState([]);
  const [history, setHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);

  const refreshHistory = async () => {
    try {
      const r = await axios.get(`${API}/history`, { params: { session_id: sessionId } });
      setHistory(r.data);
    } catch (e) { /* noop */ }
  };
  const refreshFavorites = async () => {
    try {
      const r = await axios.get(`${API}/favorites`, { params: { session_id: sessionId } });
      setFavorites(r.data);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data)).catch(() => {});
    refreshHistory();
    refreshFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFavorite = async (text, advice = "", tone = "") => {
    try {
      await axios.post(`${API}/favorites`, { session_id: sessionId, text, advice, tone });
      toast.success("Ajouté aux favoris ⭐");
      refreshFavorites();
    } catch {
      toast.error("Impossible d'enregistrer");
    }
  };

  const removeFavorite = async (id) => {
    await axios.delete(`${API}/favorites/${id}`, { params: { session_id: sessionId } });
    toast("Favori supprimé");
    refreshFavorites();
  };

  const removeHistory = async (id) => {
    await axios.delete(`${API}/history/${id}`, { params: { session_id: sessionId } });
    toast("Entrée supprimée");
    refreshHistory();
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9] text-[#0A0A0A] bg-grain">
      {/* HEADER */}
      <header
        data-testid="app-header"
        className="sticky top-0 z-40 bg-[#FFFDF9] border-b-2 border-[#0A0A0A]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2" data-testid="logo-link">
            <span className="nb-border bg-[#FF4D85] px-2 py-1 font-black text-sm uppercase">
              PSF
            </span>
            <span className="font-display font-extrabold text-lg hidden sm:inline">
              Merci du conseil
            </span>
          </a>
          <div className="flex items-center gap-2">
            <a
              href="https://instagram.com/merci.du.conseil"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-link"
              onClick={() => trackEvent("instagram_click", { source: "header" })}
              aria-label="Suivez @merci.du.conseil sur Instagram"
              className="nb-border bg-white px-3 py-2 font-bold text-sm hover:bg-gradient-to-tr hover:from-[#feda75] hover:via-[#fa7e1e] hover:to-[#d62976] hover:text-white transition-all flex items-center gap-2"
            >
              <Instagram size={16} strokeWidth={2.5} />
              <span className="hidden sm:inline">@merci.du.conseil</span>
            </a>
            <button
              data-testid="open-history-button"
              onClick={() => setPanelOpen(true)}
              className="nb-border bg-white px-3 py-2 font-bold text-sm hover:bg-[#A7F3D0] transition-colors flex items-center gap-2"
            >
              <History size={16} strokeWidth={2.5} />
              <span className="hidden sm:inline">Historique</span>
              <span className="nb-border bg-[#FCD34D] px-1.5 text-xs">{history.length}</span>
            </button>
          </div>
        </div>
      </header>

      <main id="top" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 space-y-16">
        <Hero />
        <Generator
          sessionId={sessionId}
          api={API}
          onGenerated={refreshHistory}
          onFavorite={addFavorite}
        />
        <Categories categories={categories} onFavorite={(t) => addFavorite(t)} />

        <section className="space-y-6">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight">
              Pourquoi cette appli ?
            </h2>
            <span className="nb-border bg-[#A7F3D0] px-3 py-1 text-xs font-black uppercase">
              Pour vous, par nous
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Coffee,
                title: "Café froid, patience à zéro",
                body: "Vous êtes épuisé·e. Vous n'avez pas la bande passante pour trouver LA bonne réponse. Nous si.",
                bg: "#FCD34D",
              },
              {
                icon: Sparkles,
                title: "Une IA qui prend votre défense",
                body: "Claude génère 3 répliques sur-mesure, dans le ton que vous choisissez. Vous copiez, vous envoyez.",
                bg: "#FFD6E8",
              },
              {
                icon: Star,
                title: "Sauvegardez vos punchlines",
                body: "Les bonnes réponses s'ajoutent à vos favoris. Prêtes pour le prochain dîner de famille.",
                bg: "#A7F3D0",
              },
            ].map((f, i) => (
              <article
                key={i}
                data-testid={`feature-card-${i}`}
                className="nb-card p-6"
                style={{ background: f.bg }}
              >
                <f.icon size={32} strokeWidth={2.5} />
                <h3 className="font-display font-extrabold text-xl mt-4 mb-2">{f.title}</h3>
                <p className="font-medium text-base">{f.body}</p>
              </article>
            ))}
          </div>
        </section>

        <footer className="border-t-2 border-[#0A0A0A] pt-6 pb-12 text-center space-y-3">
          <a
            href="https://instagram.com/merci.du.conseil"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-link-footer"
            onClick={() => trackEvent("instagram_click", { source: "footer" })}
            className="inline-flex items-center gap-2 nb-border bg-white px-4 py-2 font-black text-sm hover:bg-gradient-to-tr hover:from-[#feda75] hover:via-[#fa7e1e] hover:to-[#d62976] hover:text-white transition-all"
          >
            <Instagram size={16} strokeWidth={2.5} /> Suis @merci.du.conseil
          </a>
          <p className="font-bold">
            Fait avec rage & tendresse pour les parents 2026.{" "}
            <span className="nb-border bg-[#FF4D85] px-2 py-0.5 ml-2 text-xs uppercase">
              Merci du conseil
            </span>
          </p>
        </footer>
      </main>

      {/* Side panel */}
      {panelOpen && (
        <HistoryFavoritesPanel
          onClose={() => setPanelOpen(false)}
          history={history}
          favorites={favorites}
          onDeleteHistory={removeHistory}
          onDeleteFavorite={removeFavorite}
          onAddFavorite={(t, a, tone) => addFavorite(t, a, tone)}
        />
      )}
    </div>
  );
}
